import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalService } from '@services/goalService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'goals';

export const useGoals = () => {
  const queryClient = useQueryClient();

  const { data: goals = [], isLoading, error, refetch } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: goalService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: goalService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Meta criada com sucesso!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao criar meta'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ goalId, data }) => goalService.update(goalId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Meta atualizada com sucesso!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao atualizar meta'),
  });

  const deleteMutation = useMutation({
    mutationFn: goalService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Meta excluída!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao excluir meta'),
  });

  const addContributionMutation = useMutation({
    mutationFn: ({ goalId, data }) => goalService.addContribution(goalId, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      if (res?.goalCompleted) {
        toast.success('🎉 Meta concluída! Parabéns!');
      } else {
        toast.success('Aporte registrado!');
      }
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao registrar aporte'),
  });

  // Computed totals
  const totalGoals = goals.length;
  const completedGoals = goals.filter((g) => {
    const pct = g.targetAmount > 0 ? (g.currentAmount / g.targetAmount) * 100 : 0;
    return pct >= 100;
  }).length;
  const totalTargetAmount = goals.reduce((sum, g) => sum + (g.targetAmount || 0), 0);
  const totalSaved = goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);

  return {
    goals,
    totalGoals,
    completedGoals,
    totalTargetAmount,
    totalSaved,
    isLoading,
    error,
    refetch,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    addContribution: addContributionMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isAddingContribution: addContributionMutation.isPending,
  };
};

export default useGoals;
